import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  TeachingPopover,
  TeachingPopoverBody,
  TeachingPopoverFooter,
  TeachingPopoverHeader,
  TeachingPopoverSurface,
  TeachingPopoverTitle,
} from '@fluentui/react-components';
import { DefaultButton, PrimaryButton } from '@fluentui/react';

type TeachingBubblePropsType = {
  target?: string;
  text?: string;
  visible: boolean;
  primaryBtnText?: string;
  secondaryBtnText?: string;
  headline?: string;
  title?: string;
  primaryBtnAction?: () => void;
  hideSecondaryBtn?: boolean;
  onDismiss?: () => void;
};

const DEFAULT_HEADLINE = 'Guided tour';
const DEFAULT_TITLE = '';
const DEFAULT_PRIMARY_BTN_TEXT = 'Next';
const DEFAULT_SECONDARY_BTN_TEXT = 'Cancel';

const actionPrimary = (handlePrimaryButtonClick: () => void, primaryBtnText: string) => () =>
  <PrimaryButton onClick={handlePrimaryButtonClick}>{primaryBtnText}</PrimaryButton>;

const actionSecondary = (handleSecondaryButtonClick: () => void, secondaryBtnText: string) => () =>
  <DefaultButton onClick={handleSecondaryButtonClick}>{secondaryBtnText}</DefaultButton>;

export const TeachingBubble = ({
  target,
  text,
  visible,
  primaryBtnText,
  secondaryBtnText,
  headline,
  title,
  primaryBtnAction,
  hideSecondaryBtn,
  onDismiss,
}: TeachingBubblePropsType) => {
  const [teachingBubbleVisible, setTeachingBubbleVisible] = useState(visible);
  const outstandingRef = useRef();

  useEffect(() => {
    const xpathResult = document.evaluate(target, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null);
    const selectedElement = xpathResult.singleNodeValue;
    if (selectedElement) {
      outstandingRef.current = selectedElement;
    }
  }, [target, visible]);

  useEffect(() => {
    setTeachingBubbleVisible(visible);
  }, [visible]);

  const handlePrimaryButtonClick = () => {
    setTeachingBubbleVisible(false);
    primaryBtnAction?.();
  };

  const handleSecondaryButtonClick = () => {
    onDismiss?.();
    setTeachingBubbleVisible(false);
  };

  const secondaryAction = !hideSecondaryBtn
    ? {
        children: actionSecondary(handleSecondaryButtonClick, secondaryBtnText ?? DEFAULT_SECONDARY_BTN_TEXT),
      }
    : null;

  const primaryAction = {
    children: actionPrimary(handlePrimaryButtonClick, primaryBtnText ?? DEFAULT_PRIMARY_BTN_TEXT),
  };

  return (
    <TeachingPopover
      appearance="brand"
      open={teachingBubbleVisible}
      onOpenChange={(pointerEvent: React.PointerEvent, value2) => {
        const targetElement = pointerEvent.target as HTMLElement;
        const button = targetElement.closest('button');
        if (button && button.getAttribute('aria-label') === 'dismiss') {
          setTeachingBubbleVisible(value2?.open);
          onDismiss?.();
        }
      }}
      positioning={{
        target: outstandingRef.current,
      }}
    >
      <TeachingPopoverSurface>
        <TeachingPopoverHeader>{headline ?? DEFAULT_HEADLINE}</TeachingPopoverHeader>
        <TeachingPopoverBody>
          <TeachingPopoverTitle>{title ?? DEFAULT_TITLE}</TeachingPopoverTitle>
          <div>{text}</div>
        </TeachingPopoverBody>
        <TeachingPopoverFooter primary={primaryAction} secondary={secondaryAction}></TeachingPopoverFooter>
      </TeachingPopoverSurface>
    </TeachingPopover>
  );
};
